import { isBadRequestError } from '@/core/api/badRequestError';
import { FileUpload, type FileUploadHandlerEvent } from 'primereact/fileupload';
import { Image } from 'primereact/image';
import { useEffect, useRef, useState } from 'react';
import { uploadChatPhoto } from '@/entities/Media/api/chatPhotoUpload';
import { getChatPhotoValidationError } from '@/entities/Media/model/chatPhotoValidation';
import { MediaPickerButton } from '@/features/MediaLibrary/MediaPickerButton';
import type { MediaAssetResponse } from '@/core/generated/api';
import { CHAT_MESSAGE_MAX_PHOTOS } from '@/entities/ChatDialog/model/sendMessageSchema';
import { getErrorMessage } from '@/shared/lib/errors';
import { ActionButton } from '@/shared/ui/ActionButton/ActionButton';
import { RequestError, RequestLoading } from '@/shared/ui/RequestState';
import type { ChatPhotoAttachmentsProps } from './types';
import classes from './ChatPhotoAttachments.module.scss';

/** Загружает фотографии и показывает их перед отправкой */
export const ChatPhotoAttachments = (
    { value, disabled, onChange, onBusyChange }: ChatPhotoAttachmentsProps
) => {
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const generation = useRef(0);

    useEffect(() => () => { generation.current += 1; }, []);

    /** Добавляет изображение медиатеки в черновик без повторной загрузки */
    const addFromLibrary = (url: string, asset: MediaAssetResponse): void => {
        if (disabled || busy || value.length >= CHAT_MESSAGE_MAX_PHOTOS) return;

        const validationError = getChatPhotoValidationError(asset.contentType, asset.size);
        setError(validationError ?? null);

        if (validationError || value.includes(url)) return;

        onChange([...value, url]);
    };

    /** Добавляет выбранные фотографии, сохраняя успешно загруженные */
    const upload = async (event: FileUploadHandlerEvent): Promise<void> => {
        if (busy || disabled) return;
        if (value.length + event.files.length > CHAT_MESSAGE_MAX_PHOTOS) {
            setError('Можно прикрепить не больше 10 фотографий');
            event.options.clear();

            return;
        }
        const current = ++generation.current;
        const uploaded = [...value];
        setBusy(true);
        setError(null);
        onBusyChange(true);

        try {
            for (const file of event.files) {
                try {
                    const url = await uploadChatPhoto(file);

                    if (generation.current !== current) return;
                    uploaded.push(url);
                    onChange([...uploaded]);
                } catch (reason: unknown) {
                    if (generation.current !== current) return;
                    if (!isBadRequestError(reason)) {
                        setError(`${file.name}: ${getErrorMessage(reason, 'Не удалось загрузить фотографию')}`);
                    }
                }
            }
        } finally {
            if (generation.current === current) {
                event.options.clear();
                setBusy(false);
                onBusyChange(false);
            }
        }
    };

    return <div className={classes.attachments}>
        <div className={classes.toolbar}>
            <div className={classes.sources}>
                <FileUpload
                    mode="basic"
                    name="chat-photos"
                    accept="image/jpeg,image/png,image/webp"
                    multiple
                    auto
                    customUpload
                    chooseLabel="Добавить фотографии"
                    chooseOptions={{ icon: 'pi pi-images' }}
                    disabled={disabled || busy || value.length >= CHAT_MESSAGE_MAX_PHOTOS}
                    uploadHandler={(event) => void upload(event)}
                    pt={{ input: { 'aria-label': 'Добавить фотографии' } }}
                />
                <MediaPickerButton
                    disabled={disabled || busy || value.length >= CHAT_MESSAGE_MAX_PHOTOS}
                    onPick={addFromLibrary}
                />
            </div>
            <small>
                {value.length} / {CHAT_MESSAGE_MAX_PHOTOS} · JPEG, PNG, WebP до 10 МБ</small>
        </div>
        {busy &&
            <RequestLoading message="Загружаем фотографии…" />
        }
        {error &&
            <RequestError
                message={error}
                compact
            />
        }
        {value.length > 0 &&
            <div className={classes.gallery}>
                {value.map((url, index) => <div
                    key={url}
                    className={classes.photo}
                >
                    <Image
                        src={url}
                        alt={`Фотография ${index + 1} к сообщению`}
                        preview
                    />
                    <ActionButton
                        type="button"
                        className={classes.remove}
                        icon="pi pi-times"
                        iconOnly
                        size="small"
                        color="secondary"
                        aria-label={`Убрать фотографию ${index + 1}`}
                        disabled={disabled || busy}
                        onClick={() => onChange(value.filter((_, position) => position !== index))}
                    />
                </div>)}
            </div>
        }
    </div>;
};
